import React from 'react';
import {
  CloudArrowDownIcon,
  LockOpenIcon,
  FolderIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { DownloadProgress } from '../hooks/useDownloadProgress';

interface DownloadProgressModalProps {
  visible: boolean;
  progress: DownloadProgress;
  onCancel: () => void;
  onClose: () => void;
}

const DownloadProgressModal: React.FC<DownloadProgressModalProps> = ({
  visible,
  progress,
  onCancel,
  onClose,
}) => {
  const [isVisible, setIsVisible] = React.useState(false);

  React.useEffect(() => {
    if (visible) {
      document.body.style.overflow = 'hidden';
      const timer = setTimeout(() => setIsVisible(true), 10);
      return () => { document.body.style.overflow = ''; clearTimeout(timer); };
    } else {
      setIsVisible(false);
    }
  }, [visible]);

  if (!visible) return null;

  const getStepIcon = () => {
    if (progress.error) {
      return <ExclamationTriangleIcon className="h-8 w-8 text-error-500" />;
    }
    
    if (!progress.active) {
      return <CheckCircleIcon className="h-8 w-8 text-success-500" />;
    }

    switch (progress.step) {
      case 'querying':
        return <CloudArrowDownIcon className="h-8 w-8 text-info-500 animate-pulse" />;
      case 'downloading':
        return <LockOpenIcon className="h-8 w-8 text-warning-500 animate-pulse" />;
      case 'zipping':
        return <FolderIcon className="h-8 w-8 text-success-500 animate-pulse" />;
      default:
        return <CloudArrowDownIcon className="h-8 w-8 text-info-500 animate-pulse" />;
    }
  };

  const getProgressPercentage = () => {
    if (!progress.total || !progress.current) return 0;
    return Math.round((progress.current / progress.total) * 100);
  };



  const getStepTitle = () => {
    if (progress.error) {
      return 'Fehler aufgetreten';
    }
    
    if (!progress.active) {
      return 'Download abgeschlossen';
    }

    switch (progress.step) {
      case 'querying':
        return 'Dokumente werden gesucht...';
      case 'downloading':
        return 'Dokumente werden heruntergeladen...';
      case 'zipping':
        return 'ZIP-Archiv wird erstellt...';
      default:
        return 'Download läuft...';
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4">
      <div className="w-full sm:max-w-md bg-white dark:bg-[#1c2432] border-t sm:border border-gray-200 dark:border-gray-700 rounded-t-2xl sm:rounded-2xl max-h-[90vh] flex flex-col shadow-2xl animate-slide-up sm:animate-fade-in">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="w-9 h-9 rounded-xl bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-400 grid place-items-center flex-shrink-0">
            <CloudArrowDownIcon className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-gray-900 dark:text-white truncate">
              Dokumente herunterladen
            </h3>
          </div>
          {!progress.active && (
            <button
              onClick={onClose}
              className="w-9 h-9 grid place-items-center rounded-lg hover:bg-gray-100 dark:hover:bg-white/5 text-gray-500 dark:text-gray-400 flex-shrink-0"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col items-center gap-3 sm:gap-4">
          {/* Icon */}
          <div className="flex items-center justify-center">
            {getStepIcon()}
          </div>

          {/* Title and Message */}
          <div className="text-center">
            <h4 className="text-sm sm:text-base font-medium text-gray-900 dark:text-white mb-2">
              {getStepTitle()}
            </h4>
            
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-3 sm:mb-4">
              {progress.error ? progress.error : progress.message}
            </p>

            {/* Document Name */}
            {progress.documentName && progress.active && (
              <div className="w-full max-w-full">
                <p 
                  className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 sm:px-3 py-2 rounded-lg font-mono text-center truncate" 
                  title={progress.documentName}
                  style={{ minHeight: '2rem' }}
                >
                  {progress.documentName}
                </p>
              </div>
            )}
          </div>

          {/* Progress Bar */}
          {progress.active && progress.total && progress.current !== undefined && (
            <div className="w-full">
              <div className="flex justify-between items-center mb-2 text-xs sm:text-sm">
                <span className="text-gray-600 dark:text-gray-400">
                  {progress.current} von {progress.total}
                </span>
                <span className="text-gray-600 dark:text-gray-400">
                  {getProgressPercentage()}%
                </span>
              </div>
              
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div 
                  className="bg-primary-600 dark:bg-primary-500 h-2 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${getProgressPercentage()}%` }}
                />
              </div>
            </div>
          )}

          {/* Loading Spinner (when no specific progress) */}
          {progress.active && (!progress.total || progress.current === undefined) && (
            <div className="w-full flex justify-center">
              <svg 
                className="animate-spin h-5 w-5 sm:h-6 sm:w-6 text-primary-600 dark:text-primary-500" 
                viewBox="0 0 24 24"
              >
                <circle 
                  className="opacity-25" 
                  cx="12" 
                  cy="12" 
                  r="10" 
                  stroke="currentColor" 
                  strokeWidth="4" 
                />
                <path 
                  className="opacity-75" 
                  fill="currentColor" 
                  d="M4 12a8 8 0 018-8v8h8a8 8 0 01-8 8 8 8 0 01-8-8z" 
                />
              </svg>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-2 flex-wrap">
          {progress.active ? (
            <button
              onClick={onCancel}
              className="px-4 py-2 bg-error-600 hover:bg-error-700 text-white rounded-lg text-sm font-semibold transition-colors"
            >
              Abbrechen
            </button>
          ) : (
            <>
              {progress.error && (
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-sm font-semibold transition-colors"
                >
                  Schließen
                </button>
              )}
              {!progress.error && (
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-success-600 hover:bg-success-700 text-white rounded-lg text-sm font-semibold transition-colors"
                >
                  ✓ Fertig
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DownloadProgressModal;